import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { deleteCmsVehicle, getCmsBrands, getCmsVehicle, getCmsVehicles } from '../../api/client';
import VehicleEditor from './VehicleEditor';
import DataTablePagination from '../common/DataTablePagination';
import SearchableSelect from '../common/SearchableSelect';

const VehicleManagement = ({ initialBrandFilter, onClearEditState, onDirtyChange }) => {
  const { vehicleId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const brandIdFromUrl = searchParams.get('brandId');
  const isCreating = location.pathname.endsWith('/new');

  const [vehicles, setVehicles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedBrandId, setSelectedBrandId] = useState(brandIdFromUrl || initialBrandFilter || '');
  const [powertrainFilter, setPowertrainFilter] = useState('');
  const [conditionFilter, setConditionFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toastMsg, setToastMsg] = useState('');
  const [editingVehicle, setEditingVehicle] = useState(null);

  // Sync brand filter from URL
  useEffect(() => {
    if (brandIdFromUrl) {
      setSelectedBrandId(brandIdFromUrl);
    } else if (initialBrandFilter) {
      setSelectedBrandId(initialBrandFilter);
    }
  }, [brandIdFromUrl, initialBrandFilter]);

  // Load brands for dropdown
  const loadBrands = useCallback(async () => {
    try {
      const b = await getCmsBrands();
      setBrands(b);
    } catch (err) {
      console.error('Failed to load brands for filter:', err);
    }
  }, []);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  // Load vehicles list
  const loadVehicles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (selectedBrandId) params.brand_id = selectedBrandId;
      if (powertrainFilter) params.powertrain = powertrainFilter;
      if (conditionFilter) params.condition = conditionFilter;
      if (search.trim()) params.search = search.trim();

      const data = await getCmsVehicles(params);
      setVehicles(data);
    } catch (err) {
      console.error(err);
      setError('Could not load vehicles catalog.');
    } finally {
      setLoading(false);
    }
  }, [selectedBrandId, powertrainFilter, conditionFilter, search]);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  // Reset pagination when search or filters change
  const handleSearchChange = (val) => {
    setSearch(val);
    setPage(1);
  };

  const handleBrandChange = (val) => {
    setSelectedBrandId(val);
    setPage(1);
    if (val) {
      setSearchParams({ brandId: val }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const handlePowertrainChange = (val) => {
    setPowertrainFilter(val);
    setPage(1);
  };

  const handleConditionChange = (val) => {
    setConditionFilter(val);
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearch('');
    setSelectedBrandId('');
    setPowertrainFilter('');
    setConditionFilter('');
    setPage(1);
    setSearchParams({}, { replace: true });
  };

  // Safe handling of bookmarked/direct edit URLs without breaking links
  useEffect(() => {
    if (vehicleId) {
      const idNum = parseInt(vehicleId, 10);
      const found = vehicles.find((v) => v.id === idNum);
      if (found) {
        setEditingVehicle(found);
        navigate('/dashboard/marketplace/vehicles', { replace: true });
      } else {
        getCmsVehicle(idNum)
          .then((v) => {
            setEditingVehicle(v);
            navigate('/dashboard/marketplace/vehicles', { replace: true });
          })
          .catch((err) => {
            console.error('Failed to load bookmarked vehicle:', err);
            setError('Could not load vehicle details.');
            navigate('/dashboard/marketplace/vehicles', { replace: true });
          });
      }
    } else if (isCreating) {
      const targetBrandId = brandIdFromUrl
        ? parseInt(brandIdFromUrl, 10)
        : selectedBrandId
        ? parseInt(selectedBrandId, 10)
        : brands[0]?.id || '';
      setEditingVehicle({ brand_id: targetBrandId });
      navigate('/dashboard/marketplace/vehicles', { replace: true });
    }
  }, [vehicleId, isCreating, brandIdFromUrl, vehicles, brands, selectedBrandId, navigate]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toastMsg) return;
    const t = setTimeout(() => setToastMsg(''), 4000);
    return () => clearTimeout(t);
  }, [toastMsg]);

  // Modal actions without URL alteration
  const handleStartCreate = () => {
    const targetBrandId = selectedBrandId ? parseInt(selectedBrandId, 10) : brands[0]?.id || '';
    setEditingVehicle({ brand_id: targetBrandId });
  };

  const handleStartEdit = (vehicle) => {
    setEditingVehicle(vehicle);
  };

  const handleDelete = async (vehicle) => {
    const ok = window.confirm(
      `Delete "${vehicle.name}"?\n\nIt is removed from the catalogue everywhere. Existing inquiries and applications keep its name.`
    );
    if (!ok) return;
    try {
      await deleteCmsVehicle(vehicle.id);
      setToastMsg('Vehicle deleted.');
      loadVehicles();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not delete the vehicle.');
    }
  };

  const handleCancelEdit = () => {
    setEditingVehicle(null);
    if (onClearEditState) onClearEditState();
  };

  const handleSaveSuccess = (savedVehicle) => {
    loadVehicles();
    setEditingVehicle(null);
    setToastMsg('Vehicle saved successfully.');
    if (onClearEditState) onClearEditState();
  };

  const getBrandName = (brandId, mfg) => {
    if (brandId) {
      const found = brands.find((b) => b.id === brandId);
      if (found) return found.name;
    }
    if (mfg) {
      const foundSlug = brands.find((b) => b.slug === mfg.toLowerCase());
      if (foundSlug) return foundSlug.name;
      return mfg.toUpperCase();
    }
    return 'Unknown Brand';
  };

  // Pagination slicing
  const pagedVehicles = useMemo(() => {
    const start = (page - 1) * pageSize;
    return vehicles.slice(start, start + pageSize);
  }, [vehicles, page, pageSize]);

  const hasActiveFilters = Boolean(search || selectedBrandId || powertrainFilter || conditionFilter);

  return (
    <div className="cms-container">
      {/* Friendly success toast */}
      {toastMsg && (
        <div className="dash-alert success" style={{ marginBottom: '16px' }}>
          <i className="fas fa-circle-check" /> {toastMsg}
        </div>
      )}

      {/* Top Filter & Action Bar */}
      <div className="cms-filter-toolbar">
        <div className="cms-search-box">
          <i className="fas fa-search" />
          <input
            type="text"
            placeholder="Search vehicles (e.g. EV, Imported, Toyota, Automatic, Pickup…)"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {search && (
            <button type="button" className="cms-search-clear" onClick={() => handleSearchChange('')}>
              ×
            </button>
          )}
        </div>

        <div className="cms-filters-row">
          <SearchableSelect
            className="cms-select-searchable"
            triggerClassName="cms-select"
            value={selectedBrandId}
            onChange={(e) => handleBrandChange(e.target.value)}
            aria-label="Filter by brand"
            placeholder={`All Brands (${brands.length})`}
            searchPlaceholder="Search brand..."
            options={[
              { value: '', label: `All Brands (${brands.length})` },
              ...brands.map((b) => ({
                value: String(b.id),
                label: `${b.name} (${b.vehicle_count})`,
                icon: b.logo || '🚗',
              })),
            ]}
          />

          <SearchableSelect
            className="cms-select-wrap"
            value={powertrainFilter}
            onChange={(e) => handlePowertrainChange(e.target.value)}
            placeholder="All Powertrains"
            aria-label="Filter by powertrain"
            options={[
              { value: '', label: 'All Powertrains' },
              { value: 'petrol', label: 'Petrol', icon: '⛽' },
              { value: 'diesel', label: 'Diesel', icon: '🛢️' },
              { value: 'hybrid', label: 'Hybrid', icon: '🔋' },
              { value: 'ev', label: 'Electric (EV)', icon: '⚡' },
            ]}
          />

          <SearchableSelect
            className="cms-select-wrap"
            value={conditionFilter}
            onChange={(e) => handleConditionChange(e.target.value)}
            placeholder="All Conditions"
            aria-label="Filter by condition"
            options={[
              { value: '', label: 'All Conditions' },
              { value: 'new', label: 'Brand New', icon: '✨' },
              { value: 'used', label: 'Used / Pre-Owned', icon: '🏷️' },
            ]}
          />

          {hasActiveFilters && (
            <button
              type="button"
              className="dash-btn-reset-filters"
              onClick={handleResetFilters}
            >
              <i className="fas fa-rotate-left" /> Reset
            </button>
          )}

          <button type="button" className="btn-primary" onClick={handleStartCreate}>
            <i className="fas fa-plus" /> Add Vehicle
          </button>
        </div>
      </div>

      {error && (
        <div className="dash-alert" style={{ marginBottom: '16px' }}>
          <i className="fas fa-circle-exclamation" /> {error}
        </div>
      )}

      {/* Vehicles Table / List */}
      <div className="cms-section-card">
        <div className="cms-section-head">
          <h3>
            <i className="fas fa-car" /> Vehicle Catalog ({vehicles.length})
          </h3>
          <span className="cms-text-muted">
            {selectedBrandId ? `Filtered by ${getBrandName(parseInt(selectedBrandId, 10))}` : 'All registered models'}
          </span>
        </div>

        {loading ? (
          <div className="dash-loading inline">
            <div className="dash-spinner" />
            <p>Loading vehicle records…</p>
          </div>
        ) : vehicles.length === 0 ? (
          <div className="dash-empty">
            <div className="dash-empty-icon">
              <i className="fas fa-car" />
            </div>
            <h3>No vehicles found</h3>
            <p>
              {hasActiveFilters
                ? 'No vehicle records matched your search and filter criteria.'
                : 'No vehicles exist in the database. Add your first vehicle model.'}
            </p>
            {hasActiveFilters ? (
              <button
                type="button"
                className="dash-btn-reset-filters"
                style={{ marginTop: '14px' }}
                onClick={handleResetFilters}
              >
                <i className="fas fa-rotate-left" /> Clear filters
              </button>
            ) : (
              <button
                type="button"
                className="btn-primary"
                style={{ marginTop: '12px' }}
                onClick={handleStartCreate}
              >
                <i className="fas fa-plus" /> Add Vehicle
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="cms-table-wrap">
              <table className="cms-table">
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>Brand</th>
                    <th>Price</th>
                    <th>Specs Overview</th>
                    <th>Powertrain</th>
                    <th>Origin</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedVehicles.map((v) => (
                    <tr key={v.id}>
                      <td>
                        <div className="cms-tbl-vehicle">
                          <div className="cms-tbl-thumb-wrap">
                            {v.image_url ? (
                              <img src={v.image_url} alt={v.name} className="cms-tbl-thumb" />
                            ) : (
                              <span className="cms-tbl-logo">{v.logo || '🚗'}</span>
                            )}
                          </div>
                          <div>
                            <strong>{v.name}</strong>
                            <div className="cms-tbl-submeta">
                              <span>{v.variant || 'Standard'}</span>
                              {v.model_year && <span className="cms-year-chip">{v.model_year}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="cms-brand-pill">
                          {getBrandName(v.brand_id, v.mfg)}
                        </span>
                      </td>
                      <td>
                        <strong className="cms-price-highlight">{v.price}</strong>
                      </td>
                      <td>
                        <div className="cms-specs-summary">
                          {v.engine && v.engine !== 'N/A' && (
                            <span><i className="fas fa-gauge-simple-high" /> {v.engine}</span>
                          )}
                          {v.power && v.power !== 'N/A' && (
                            <span><i className="fas fa-bolt" /> {v.power}</span>
                          )}
                          {v.range_km && (
                            <span><i className="fas fa-road" /> {v.range_km} km</span>
                          )}
                          {v.battery_kwh && (
                            <span><i className="fas fa-car-battery" /> {v.battery_kwh} kWh</span>
                          )}
                          {v.fuel_efficiency && (
                            <span><i className="fas fa-gas-pump" /> {v.fuel_efficiency} km/L</span>
                          )}
                          {v.available_colors && (
                            <span title={v.available_colors}>
                              <i className="fas fa-palette" /> {v.available_colors.split(',').length} colors
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`cms-powertrain-pill ${v.powertrain || 'petrol'}`}>
                          {v.powertrain || 'petrol'}
                        </span>
                      </td>
                      <td>
                        <span className={`cms-origin-tag ${v.origin || 'assembled'}`}>
                          {v.origin === 'chinese' ? 'Chinese' : v.origin === 'assembled' ? 'Assembled' : v.origin || 'Imported'}
                        </span>
                      </td>
                      <td>
                        <div className="cms-row-actions">
                          <button
                            type="button"
                            className="btn-edit-sm"
                            onClick={() => handleStartEdit(v)}
                          >
                            <i className="fas fa-pen-to-square" /> Edit Specs
                          </button>
                          <button
                            type="button"
                            className="btn-delete-sm"
                            onClick={() => handleDelete(v)}
                            aria-label={`Delete ${v.name}`}
                          >
                            <i className="fas fa-trash-can" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <DataTablePagination
              currentPage={page}
              pageSize={pageSize}
              totalItems={vehicles.length}
              onPageChange={setPage}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(1);
              }}
            />
          </>
        )}
      </div>

      {/* Large Modal Editor Dialog over listing backdrop */}
      {editingVehicle !== null && (
        <VehicleEditor
          vehicle={editingVehicle}
          brands={brands}
          onSaveSuccess={handleSaveSuccess}
          onCancel={handleCancelEdit}
          onDirtyChange={onDirtyChange}
        />
      )}
    </div>
  );
};

export default VehicleManagement;
